"use client";

import { useState, useEffect, useMemo, useCallback } from "react";
import { DatesSetArg } from "@fullcalendar/core/index.js";
import { getTranslation } from "@/i18n/get-translation";
import { getAvailability } from "@/services/agenda.service";
import {
  IAppointmentCalendar,
  IDoctorAvailability,
  IDoctorAvailabilityTime,
  IDoctorAvailabilityResponse,
  IAppointmentPayload,
} from "@/types/agenda.type";
import MOMENTJS_DATE_FORMATES from "@/common/constants/momentjs-format";
import { getTimeStops } from "@/utils/get-time-stops";
import dayjs, { Dayjs } from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import { useSnackbar } from "notistack";

dayjs.extend(isoWeek);

interface ISlots {
  [key: string]: string[];
}

export default function AppointmentCalendar({
  locale,
  setSelectedDateTime,
  setAppStep,
  doctorId,
  reasons,
  selectedReason,
  setSelectedReason,
  appointmentPayload,
  setAppointmentPayload,
  selectedDateTime,
}: IAppointmentCalendar) {
  const t = getTranslation(locale, "appointment");
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [currentDates, setCurrentDates] = useState<DatesSetArg>();
  const [slots, setSlots] = useState<ISlots>({});
  const [weekOffset, setWeekOffset] = useState<number>(0);
  const [showAllAppointments, setShowAllAppointments] =
    useState<boolean>(false);

  const [availabilities, setAvailabilities] = useState<IDoctorAvailability>();

  const [availableSlot, setAvailableSlot] = useState<string>();
  
  const weekDays: Dayjs[] = useMemo(() => {
    const startOfWeek = dayjs()
      .add(weekOffset, "week")
      .startOf("week")
      .add(1, "day"); // Monday

    return [...Array(7)].map((_, i) => startOfWeek.add(i, "day"));
  }, [weekOffset]);

  const handleJumpToAvailableSlots = useCallback(() => {
    if(availableSlot) {
      const todayStartWeek = dayjs().startOf("week").add(1, "day"); // Monday
      const diffWeeks = dayjs(availableSlot).startOf("week").add(1, "day").diff(todayStartWeek, "week");
      setWeekOffset(diffWeeks);
    }
  }, [availableSlot]);

  const handleGetAvailabilities = useCallback(
    (sDate: string, eDate: string, moveToDate?: boolean) => {
      if (doctorId && selectedReason) {
        setShowAllAppointments(false);
        setIsLoading(true);
        getAvailability(doctorId.toString(), sDate, eDate, selectedReason.duration)
          .then((res: IDoctorAvailabilityResponse) => {
            setAvailabilities(res.availabilities);
            if(res.next_available_date) {
              setAvailableSlot(res.next_available_date);
            }
          })
          .catch((error: Error) => {
            if (error?.message) {
              enqueueSnackbar(error.message, { variant: "error" });
            }
          }).finally(()=>{
            setIsLoading(false);
          });
      }
    },
    [availabilities, doctorId, selectedReason]
  );

  const createSlots = useCallback(() => {
    if (availabilities) {
      let tempArr: { [key: string]: string[] } = {};
      availabilities &&
        Object.entries(availabilities).forEach(
          ([date, value]: [string, IDoctorAvailabilityTime[]]) => {
            if (!tempArr[date]) {
              tempArr[date] = [];
            }
            value.forEach(({ start_time, end_time }) => {
              const newArr = [...getTimeStops(
                date,
                start_time,
                end_time,
                selectedReason?.duration
              )];
              const futureSlots = newArr.filter(time=>{
                const currentSlot: Dayjs = dayjs(`${date} ${time}`, MOMENTJS_DATE_FORMATES.toDateTimeWithoutss);
                currentSlot.isBefore(dayjs());
                return !currentSlot.isBefore(dayjs());
              })
              tempArr[date].push(
                ...futureSlots
              );
            });
          }
        );
      setSlots(tempArr);
    }
  }, [availabilities, selectedReason]);

  const maxLength = useMemo(() => {
    return Math.max(...Object.values(slots).map((arr) => arr.length), 5);
  }, [slots]);

  useEffect(() => {
    if (availabilities) {
      createSlots();
    }
  }, [availabilities, createSlots]);

  useEffect(() => {
    if (weekDays?.length) {
      let initialDate: dayjs.Dayjs = weekDays[0];
      if(initialDate.isSame(dayjs(), 'day') || initialDate.isBefore(dayjs(), 'day')) {
        initialDate = dayjs();
      }
      const ed = weekDays[weekDays.length - 1].format(
        MOMENTJS_DATE_FORMATES.toDate
      );
      handleGetAvailabilities(
        dayjs(initialDate).format(MOMENTJS_DATE_FORMATES.toDateTimeWithss),
        dayjs(`${ed} 23:59:59`).format(MOMENTJS_DATE_FORMATES.toDateTimeWithss)
      );
    }
  }, [weekDays, selectedReason]);

  return (
    <div className="relative mt-4">
      <div className="-mx-6 relative min-h-66 md:min-h-80">
        <div>
          <div className="w-full">
            {/* Grid */}
            <div className="flex w-full">
              <div className="flex-none">
                <button
                  onClick={() => setWeekOffset((p) => p - 1)}
                  className="h-9 w-7 md:h-11 md:w-8 rounded-lg flex items-center justify-center disabled:opacity-20"
                  disabled={weekDays[0].isBefore(dayjs())}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 14 9"
                    className="w-4 md:w-5 rotate-90"
                  >
                    <path
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeWidth="2"
                      d="m1 1 6 6 6-6"
                    ></path>
                  </svg>
                </button>
              </div>
              <div
                className={`flex-auto grid grid-cols-7 overflow-hidden ${
                  !showAllAppointments && "h-[13.4rem] md:h-[16.5rem]"
                }`}
              >
                {weekDays.map((d) => {
                  const key = d.format(MOMENTJS_DATE_FORMATES.toDate);
                  const daySlots = slots[key] || [];

                  return (
                    <div key={key} className="flex flex-col items-center">
                      <div className="text-center mb-2 pb-2 border-b border-b-gray-200 w-full text-xs md:text-base">
                        <div className="font-bold">{t(`weekdays.${d.format("ddd")}`)}</div>
                        <div className="opacity-70">
                          {d.format("DD")}
                          <div className="block md:inline">
                            {t(`month.${d.format("MMM")}`)}
                          </div>
                        </div>
                      </div>

                      <div className="px-[0.125rem] md:px-1 w-full">
                        {daySlots.map((slot, i) => (
                          <div key={`${key}-${i}`} className="h-[1.875rem] md:h-10 w-full py-1">
                            <button
                              className={`w-full h-full flex items-center justify-center border border-agenda-blue-600 rounded-md active:scale-95 text-xs md:text-base ${
                                dayjs(selectedDateTime).format(
                                  MOMENTJS_DATE_FORMATES.toDateTimeWithoutss
                                ) ===
                                dayjs(
                                  `${d.format(
                                    MOMENTJS_DATE_FORMATES.toDate
                                  )} ${slot}`
                                ).format(
                                  MOMENTJS_DATE_FORMATES.toDateTimeWithoutss
                                )
                                  ? "bg-agenda-blue-600 text-white hover:bg-agenda-blue-700"
                                  : "bg-transparent text-agenda-blue-600 hover:bg-blue-50"
                              }`}
                              onClick={() => {
                                setSelectedDateTime(
                                  dayjs(`${key} ${slot}`).format(
                                    MOMENTJS_DATE_FORMATES.toDateTimeWithoutss
                                  )
                                );
                                
                                setAppStep(3);
                              }}
                            >
                              {slot}
                            </button>
                          </div>
                        ))}

                        {
                          Array.from({
                            length:
                              maxLength - daySlots.length,
                          }).map((_, i) => (
                            <div
                              key={`empty-${i}`}
                              className="h-[1.875rem] md:h-10 w-full flex items-center justify-center"
                            >
                              <div className="mx-auto w-4 border-b border-b-agenda-grey-200"></div>
                            </div>
                          ))
                        }
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="flex-none">
                <button
                  onClick={() => setWeekOffset((p) => p + 1)}
                  className="h-9 w-7 md:h-11 md:w-8 rounded-lg flex items-center justify-center"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 14 9"
                    className="w-4 md:w-5 -rotate-90"
                  >
                    <path
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeWidth="2"
                      d="m1 1 6 6 6-6"
                    ></path>
                  </svg>
                </button>
              </div>
            </div>
            {
              !showAllAppointments && maxLength > 5 && (
                <div className="text-center mt-2 pt-2 border-t border-t-gray-200 w-full">
                  <button
                    onClick={() => setShowAllAppointments(true)}
                    className="text-agenda-blue-600 block underline mx-auto text-sm md:text-base"
                  >
                    {t("view_more_appointments")}
                  </button>
                </div>
              )
            }
          </div>
        </div>
        {
          !Object.values(slots).some(times => times.length > 0) && (
            <div className="absolute inset-2 top-12 md:top-14 bg-agenda-blue-50 flex items-center justify-center rounded-xl">
              {
                availableSlot ? (
                  <button onClick={()=>{handleJumpToAvailableSlots()}} className="text-agenda-blue-600 underline font-bold text-sm md:text-lg">{t("view_slots")} {dayjs(availableSlot).format(MOMENTJS_DATE_FORMATES.toDate)}</button>
                ) : (
                  <div className="font-bold text-sm md:text-lg">{t("no_appointment")}</div>
                )
              }
            </div>
          )
        }
        {
          isLoading && (
            <div className="absolute inset-2 top-13 md:top-14 bg-agenda-blue-50 flex items-center justify-center rounded-xl">
              <div className="loader"></div>
            </div>
          )
        }
      </div>
      
    </div>
  );
}
